import type { ReactNode } from "react";

export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** exact value shown on hover */
  title?: string;
  tone?: "default" | "buy" | "sell" | "ok" | "danger" | "warn" | "muted";
  className?: string;
}

const TONE: Record<NonNullable<StatProps["tone"]>, string> = {
  default: "text-fg",
  buy: "text-buy",
  sell: "text-sell",
  ok: "text-ok",
  danger: "text-danger",
  warn: "text-warn",
  muted: "text-fg-muted",
};

/** The plain-text parts of a stat, for the hover title when none is given. */
function textOf(...parts: ReactNode[]): string | undefined {
  const text = parts.filter((p) => typeof p === "string" || typeof p === "number").join(" · ");
  return text || undefined;
}

/** A value is never cut (it wraps); a sub shows two lines, the rest on hover. */
export function Stat({ label, value, sub, title, tone = "default", className = "" }: StatProps) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 ${className}`}
      title={title ?? (sub === undefined ? undefined : textOf(label, value, sub))}
    >
      <div className="truncate text-2xs uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={`num text-sm font-semibold leading-tight break-words ${TONE[tone]}`}>{value}</div>
      {sub !== undefined && <div className="line-clamp-2 text-2xs break-words text-fg-muted">{sub}</div>}
    </div>
  );
}

/** A row of stats. */
export function StatRow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-x-3 gap-y-2 ${className}`}>
      {children}
    </div>
  );
}
